/**
 * Dashboard - shows user profile and matrix list
 */

import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Toast } from '../components/Toast';

interface UserProfile {
  id: number;
  email: string;
  full_name: string | null;
  subscription_status: string;
  subscription_type: string | null;
  free_matrices_used: number;
  free_matrices_limit: number;
  monthly_matrices_used: number;
  monthly_matrices_limit: number;
  monthly_quota_reset_at: string | null;
  subscription_ends_at: string | null;
}

interface MatrixSummary {
  request_uuid: string;
  created_at: string;
  status: string; // pending, completed, failed
  project_params: {
    project_name?: string;
    state: string;
    county: string;
    city?: string;
    power_capacity_mw: number;
    project_type: string;
  };
}

export const Dashboard: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [matrices, setMatrices] = useState<MatrixSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<{ message: string; uuid: string } | null>(null);
  const [showFailedMatrices, setShowFailedMatrices] = useState(true);
  const previousPendingUuids = useRef<Set<string>>(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh every 15 seconds if there are pending matrices
  useEffect(() => {
    const hasPending = matrices.some(m => m.status === 'pending');

    if (!hasPending) {
      return; // No polling needed
    }

    const refreshInterval = setInterval(() => {
      loadData();
    }, 15000);

    return () => clearInterval(refreshInterval);
  }, [matrices]);

  const loadData = async () => {
    try {
      const [profileData, matricesData] = await Promise.all([
        apiClient.getUserProfile(),
        apiClient.listMatrices(50, 0),
      ]);
      setProfile(profileData);

      const newMatrices: MatrixSummary[] = matricesData.requests || [];

      // Check if any previously pending matrices have completed
      const currentPendingUuids = new Set(
        newMatrices.filter(m => m.status === 'pending').map(m => m.request_uuid)
      );

      // Find matrices that were pending but are now completed
      previousPendingUuids.current.forEach(uuid => {
        if (!currentPendingUuids.has(uuid)) {
          const completedMatrix = newMatrices.find(m => m.request_uuid === uuid);
          if (completedMatrix && completedMatrix.status === 'completed') {
            const projectName = completedMatrix.project_params.project_name ||
                               `${completedMatrix.project_params.county} Project`;
            setToast({
              message: `Matrix "${projectName}" is ready!`,
              uuid: uuid
            });
          }
        }
      });

      // Update the ref with current pending UUIDs
      previousPendingUuids.current = currentPendingUuids;

      setMatrices(newMatrices);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const returnUrl = window.location.href;
      const { portal_url } = await apiClient.createPortalSession(returnUrl);
      window.location.href = portal_url;
    } catch (err) {
      alert('Failed to open subscription portal. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <img src="/logo-primary.svg" alt="Loading" className="h-16 w-16 ring-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-24">
        <div className="card text-center">
          <p className="text-text-secondary">{error}</p>
        </div>
      </div>
    );
  }

  const canGenerateMatrix =
    ((profile?.subscription_status === 'active' || profile?.subscription_status === 'canceled') &&
     profile.monthly_matrices_used < profile.monthly_matrices_limit) ||
    (profile?.subscription_status === 'free' && profile.free_matrices_used < profile.free_matrices_limit);

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          onClose={() => setToast(null)}
          onClick={() => {
            navigate(`/matrix/${toast.uuid}`);
            setToast(null);
          }}
        />
      )}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
      {/* Header with Action */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 md:mb-16">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold mb-2">Dashboard</h1>
          <p className="text-text-secondary font-mono text-xs md:text-sm break-all">{profile?.email}</p>
        </div>
        {canGenerateMatrix ? (
          <Link to="/create" className="btn-primary text-sm md:text-base whitespace-nowrap">
            Generate Matrix
          </Link>
        ) : profile?.subscription_status === 'active' ? (
          <button
            onClick={handleManageSubscription}
            className="btn-secondary text-sm md:text-base whitespace-nowrap"
          >
            Manage Subscription
          </button>
        ) : (
          <Link to="/pricing" className="btn-secondary text-sm md:text-base whitespace-nowrap">
            Upgrade
          </Link>
        )}
      </div>

      {/* Account Status - High Density Info */}
      <div className="card mb-8 md:mb-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
          {/* 1. PLAN */}
          <div>
            <div className="text-text-muted text-xs font-mono mb-1">PLAN</div>
            <div className="font-bold text-sm md:text-base">
              {profile?.subscription_status === 'free' ? 'FREE' :
               profile?.subscription_type ? 'PRO' : 'FREE'}
            </div>
            {/* Show status badge */}
            <div className={`text-xs font-mono mt-1 ${
              profile?.subscription_status === 'free' ? 'text-text-muted' :
              profile?.subscription_status === 'canceled' ? 'text-yellow-400' : 'text-green-400'
            }`}>
              {profile?.subscription_status === 'free' ? '' :
               profile?.subscription_status === 'canceled' ? 'ACTIVE (ENDING)' : 'ACTIVE'}
            </div>
          </div>

          {/* 2. EXPIRATION (for paid) or MATRICES (for free) */}
          {profile?.subscription_status === 'free' ? (
            <div>
              <div className="text-text-muted text-xs font-mono mb-1">FREE MATRICES</div>
              <div className="font-mono text-sm md:text-base">
                {profile.free_matrices_used} / {profile.free_matrices_limit}
              </div>
            </div>
          ) : profile?.subscription_ends_at ? (
            <div>
              <div className="text-text-muted text-xs font-mono mb-1">
                {profile.subscription_status === 'canceled' ? 'DOWNGRADES ON' : 'RENEWS ON'}
              </div>
              <div className="font-mono text-xs md:text-sm">
                {new Date(profile.subscription_ends_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </div>
              {profile.subscription_status === 'canceled' && (
                <div className="text-xs text-yellow-400 mt-1">
                  to Free Plan
                </div>
              )}
            </div>
          ) : null}

          {/* 3. MONTHLY MATRICES (paid only) */}
          {(profile?.subscription_status === 'active' || profile?.subscription_status === 'canceled') && profile.subscription_type && (
            <div>
              <div className="text-text-muted text-xs font-mono mb-1">MONTHLY MATRICES</div>
              <div className="font-mono text-sm md:text-base">
                {profile.monthly_matrices_used} / {profile.monthly_matrices_limit}
              </div>
              {profile.monthly_quota_reset_at && (
                <div className="text-xs text-text-muted mt-1">
                  Resets {new Date(profile.monthly_quota_reset_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
              )}
            </div>
          )}

          {/* Manage Link */}
          <div className="col-span-2 md:col-span-4 text-left md:text-right mt-2 md:mt-0">
            {profile?.subscription_status === 'free' ? (
              <Link to="/pricing" className="text-xs md:text-sm text-text-secondary hover:text-white transition-colors">
                Upgrade →
              </Link>
            ) : (
              <button
                onClick={handleManageSubscription}
                className="text-xs md:text-sm text-text-secondary hover:text-white transition-colors"
              >
                Manage →
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Matrices Table - High Density */}
      <div>
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h2 className="text-lg md:text-xl font-bold">Matrices</h2>
          {matrices.some(m => m.status === 'failed') && (
            <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={showFailedMatrices}
                onChange={(e) => setShowFailedMatrices(e.target.checked)}
                className="w-4 h-4"
              />
              <span>Show failed</span>
            </label>
          )}
        </div>
        {matrices.length === 0 ? (
          <div className="card text-center py-12 md:py-16">
            <p className="text-text-secondary mb-6 text-sm md:text-base">No matrices generated yet.</p>
            {canGenerateMatrix && (
              <Link to="/create" className="btn-primary text-sm md:text-base">
                Generate Your First Matrix
              </Link>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block card">
              <table className="matrix-table">
                <thead>
                  <tr>
                    <th>PROJECT</th>
                    <th>LOCATION</th>
                    <th>CAPACITY</th>
                    <th>TYPE</th>
                    <th>CREATED</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {matrices
                    .filter(m => showFailedMatrices || m.status !== 'failed')
                    .map((matrix) => (
                    <tr
                      key={matrix.request_uuid}
                      className={`transition-colors ${
                        matrix.status === 'failed'
                          ? 'opacity-60 cursor-not-allowed'
                          : 'cursor-pointer hover:bg-bg-primary'
                      }`}
                      onClick={() => matrix.status !== 'failed' && navigate(`/matrix/${matrix.request_uuid}`)}
                    >
                      <td>
                        <div className="flex items-center gap-2">
                          {matrix.status === 'pending' && (
                            <img src="/logo-primary.svg" alt="Generating" className="h-4 w-4 ring-pulse" />
                          )}
                          {matrix.status === 'failed' && (
                            <span className="text-red-500 font-bold" title="Generation failed">✕</span>
                          )}
                          {matrix.project_params.project_name || `${matrix.project_params.county} Project`}
                        </div>
                      </td>
                      <td className="text-text-secondary">
                        {matrix.project_params.city && `${matrix.project_params.city}, `}
                        {matrix.project_params.county}, {matrix.project_params.state}
                      </td>
                      <td>{matrix.project_params.power_capacity_mw} MW</td>
                      <td className="text-text-secondary">
                        {matrix.project_params.project_type?.replace('_', ' ')}
                      </td>
                      <td className="text-text-muted">
                        {matrix.status === 'pending' ? (
                          <span className="text-amber-400 font-mono text-xs">Generating...</span>
                        ) : matrix.status === 'failed' ? (
                          <span className="text-red-500 font-mono text-xs">Failed</span>
                        ) : (
                          new Date(matrix.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        )}
                      </td>
                      <td className="text-right text-text-secondary">→</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-3">
              {matrices
                .filter(m => showFailedMatrices || m.status !== 'failed')
                .map((matrix) => (
                <div
                  key={matrix.request_uuid}
                  className={`card transition-colors ${
                    matrix.status === 'failed'
                      ? 'opacity-60 cursor-not-allowed'
                      : 'cursor-pointer hover:border-white'
                  }`}
                  onClick={() => matrix.status !== 'failed' && navigate(`/matrix/${matrix.request_uuid}`)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="font-bold mb-1 text-sm flex items-center gap-2">
                        {matrix.status === 'pending' && (
                          <img src="/logo-primary.svg" alt="Generating" className="h-4 w-4 ring-pulse" />
                        )}
                        {matrix.status === 'failed' && (
                          <span className="text-red-500 font-bold" title="Generation failed">✕</span>
                        )}
                        {matrix.project_params.project_name || `${matrix.project_params.county} Project`}
                      </div>
                      <div className="text-text-secondary text-xs font-mono">
                        {matrix.project_params.city && `${matrix.project_params.city}, `}
                        {matrix.project_params.county}, {matrix.project_params.state}
                      </div>
                    </div>
                    <div className="text-text-secondary text-lg">→</div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div>
                      <div className="text-text-muted font-mono mb-1">CAPACITY</div>
                      <div className="font-mono">{matrix.project_params.power_capacity_mw} MW</div>
                    </div>
                    <div>
                      <div className="text-text-muted font-mono mb-1">TYPE</div>
                      <div className="text-text-secondary capitalize">
                        {matrix.project_params.project_type?.replace('_', ' ')}
                      </div>
                    </div>
                    <div>
                      <div className="text-text-muted font-mono mb-1">STATUS</div>
                      <div className={
                        matrix.status === 'pending' ? 'text-amber-400' :
                        matrix.status === 'failed' ? 'text-red-500' : 'text-text-muted'
                      }>
                        {matrix.status === 'pending' ? 'Generating...' :
                         matrix.status === 'failed' ? 'Failed' : (
                          new Date(matrix.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: '2-digit',
                          })
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
};
